/**
 * Poki SDK Integration
 * 
 * Provides a safe wrapper around the Poki SDK with stubs
 * for local development and testing.
 * 
 * @see https://sdk.poki.com/
 */

// Poki SDK types
interface PokiSDKType {
  init: () => Promise<void>;
  gameLoadingStart: () => void;
  gameLoadingFinished: () => void;
  gameplayStart: () => void;
  gameplayStop: () => void;
  commercialBreak: () => Promise<void>;
  rewardedBreak: () => Promise<boolean>;
  setDebug: (enabled: boolean) => void;
  getURLParam: (key: string) => string | null;
  shareableURL: (params?: Record<string, string>) => Promise<string>;
}

declare global {
  interface Window {
    PokiSDK?: PokiSDKType;
  }
}

/**
 * Poki SDK Wrapper
 * 
 * Provides safe access to Poki SDK features with fallback stubs.
 * All methods are safe to call even if SDK is not loaded.
 */
export class PokiSDK {
  private static instance: PokiSDK;
  private sdk: PokiSDKType | null = null;
  private isInitialized = false;
  
  private constructor() {}
  
  /**
   * Get singleton instance
   */
  static getInstance(): PokiSDK {
    if (!PokiSDK.instance) {
      PokiSDK.instance = new PokiSDK();
    }
    return PokiSDK.instance;
  }
  
  /**
   * Initialize SDK connection
   * Safe to call multiple times
   */
  async init(): Promise<boolean> {
    if (this.isInitialized) {
      return this.sdk !== null;
    }
    
    this.isInitialized = true;
    
    try {
      // Check if SDK is available
      if (window.PokiSDK) {
        this.sdk = window.PokiSDK;
        
        // Initialize SDK
        await this.sdk.init();
        
        console.log('[PokiSDK] SDK initialized successfully');
        return true;
      }
      
      console.log('[PokiSDK] SDK not available, using stubs');
      return false;
    } catch (error) {
      console.warn('[PokiSDK] Failed to initialize:', error);
      return false;
    }
  }
  
  /**
   * Check if SDK is available
   */
  isAvailable(): boolean {
    return this.sdk !== null;
  }
  
  /**
   * Notify SDK that game loading has started
   * Call at the very start of asset loading
   */
  gameLoadingStart(): void {
    if (this.sdk) {
      try {
        this.sdk.gameLoadingStart();
        console.log('[PokiSDK] Game loading started');
      } catch (error) {
        console.warn('[PokiSDK] gameLoadingStart failed:', error);
      }
    }
  }
  
  /**
   * Notify SDK that game loading has finished
   * Call when all assets are loaded and game is ready
   */
  gameLoadingFinished(): void {
    if (this.sdk) {
      try {
        this.sdk.gameLoadingFinished();
        console.log('[PokiSDK] Game loading finished');
      } catch (error) {
        console.warn('[PokiSDK] gameLoadingFinished failed:', error);
      }
    }
  }
  
  /**
   * Notify SDK that gameplay has started
   * Call when player starts actual gameplay (not menus)
   */
  gameplayStart(): void {
    if (this.sdk) {
      try {
        this.sdk.gameplayStart();
        console.log('[PokiSDK] Gameplay started');
      } catch (error) {
        console.warn('[PokiSDK] gameplayStart failed:', error);
      }
    }
  }
  
  /**
   * Notify SDK that gameplay has stopped
   * Call when player dies, pauses, or returns to menu
   */
  gameplayStop(): void {
    if (this.sdk) {
      try {
        this.sdk.gameplayStop();
        console.log('[PokiSDK] Gameplay stopped');
      } catch (error) {
        console.warn('[PokiSDK] gameplayStop failed:', error);
      }
    }
  }
  
  /**
   * Request a commercial break (interstitial ad)
   * Game should pause during this
   * @returns Promise that resolves when ad is done
   */
  async commercialBreak(): Promise<void> {
    if (!this.sdk) {
      // No SDK, simulate ad completion
      console.log('[PokiSDK] Commercial break requested - no SDK, simulating');
      await new Promise(resolve => setTimeout(resolve, 500));
      return;
    }
    
    try {
      console.log('[PokiSDK] Commercial break started');
      await this.sdk.commercialBreak();
      console.log('[PokiSDK] Commercial break finished');
    } catch (error) {
      console.warn('[PokiSDK] commercialBreak failed:', error);
    }
  }
  
  /**
   * Request a rewarded break (rewarded video ad)
   * Player chooses to watch for a reward
   * @returns Promise that resolves to true if reward should be given
   */
  async rewardedBreak(): Promise<boolean> {
    if (!this.sdk) {
      // No SDK, simulate successful reward
      console.log('[PokiSDK] Rewarded break requested - no SDK, simulating success');
      await new Promise(resolve => setTimeout(resolve, 500));
      return true;
    }
    
    try {
      console.log('[PokiSDK] Rewarded break started');
      const rewarded = await this.sdk.rewardedBreak();
      console.log(`[PokiSDK] Rewarded break finished, rewarded: ${rewarded}`);
      return rewarded;
    } catch (error) {
      console.warn('[PokiSDK] rewardedBreak failed:', error);
      return false;
    }
  }
  
  /**
   * Get URL parameter value
   * @param key - Parameter name
   * @returns Parameter value or null
   */
  getURLParam(key: string): string | null {
    if (this.sdk) {
      try {
        return this.sdk.getURLParam(key);
      } catch (error) {
        console.warn('[PokiSDK] getURLParam failed:', error);
      }
    }
    
    // Fallback to native URL parsing
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(key);
  }
  
  /**
   * Enable/disable debug mode
   * @param enabled - Whether to enable debug mode
   */
  setDebug(enabled: boolean): void {
    if (this.sdk) {
      try {
        this.sdk.setDebug(enabled);
        console.log(`[PokiSDK] Debug mode: ${enabled}`);
      } catch (error) {
        console.warn('[PokiSDK] setDebug failed:', error);
      }
    }
  }
}

export default PokiSDK;
